/** Legacy trailing `<!-- zw-comments ... -->` store (pre-`.zwdoc`; read-only). */

import type { CommentStore } from "../comments";

/** Line-anchored store (preferred). */
const STORE_BLOCK_RE =
  /(?:^|\n)(<!--\s*zw-comments\s*\n)([\s\S]*?)(\n?-->)(?=\n|$)/;
/** Mid-line / corrupted store (e.g. prose typed onto the opener). */
const STORE_BLOCK_LOOSE_RE =
  /(<!--\s*zw-comments\s*\n)([\s\S]*?)(\n?-->)(?=\n|$)/;

export function findStoreRange(
  doc: string,
): { from: number; to: number; json: string } | null {
  const anchored = STORE_BLOCK_RE.exec(doc);
  const match = anchored ?? STORE_BLOCK_LOOSE_RE.exec(doc);
  if (match === null || match.index === undefined) return null;
  const open = match[1];
  const json = match[2];
  const close = match[3];
  if (open === undefined || json === undefined || close === undefined) {
    return null;
  }
  const from = match[0].startsWith("\n") ? match.index + 1 : match.index;
  const to = from + open.length + json.length + close.length;
  return { from, to, json };
}

export function parseCommentStore(doc: string): CommentStore {
  const range = findStoreRange(doc);
  if (range === null) return {};
  try {
    const parsed = JSON.parse(range.json) as CommentStore;
    if (parsed === null || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
}

export function serializeStoreBlock(store: CommentStore): string {
  const json = JSON.stringify(store, null, 2);
  return `<!-- zw-comments\n${json}\n-->`;
}

export function removeStoreBlock(doc: string): string {
  const range = findStoreRange(doc);
  if (range === null) return doc;
  let from = range.from;
  let to = range.to;
  if (from > 0 && doc[from - 1] === "\n") from -= 1;
  if (to < doc.length && doc[to] === "\n") to += 1;
  return doc.slice(0, from) + doc.slice(to);
}
